import { useRef, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';

/** A named view: a set of URL params that narrows a list (a system view). */
export interface ListView {
  readonly id: string;
  readonly label: string;
  /** The params this view sets; a param the view does not name counts as absent. */
  readonly params: Readonly<Record<string, string>>;
}

/**
 * The view of `views` whose params equal the current ones, looking only at the keys that views
 * name; `undefined` when none matches (the list is in a custom state).
 */
export function activeView(
  views: readonly ListView[],
  current: { get: (key: string) => string | null },
): ListView | undefined {
  const keys = new Set(views.flatMap((view) => Object.keys(view.params)));
  return views.find((view) =>
    [...keys].every((key) => (current.get(key) ?? '') === (view.params[key] ?? '')),
  );
}

/**
 * A bar of named views (decision 182, U6). The active one comes from the current params, so a
 * shared link shows the right tab. Tabs pattern: one tab in the tab order, arrow keys, Home and
 * End move and select.
 */
export function ViewTabs({
  label,
  views,
  current,
  onSelect,
}: {
  readonly label: string;
  readonly views: readonly ListView[];
  readonly current: { get: (key: string) => string | null };
  readonly onSelect: (view: ListView) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const active = activeView(views, current);
  // With no match, the first tab stays reachable by keyboard.
  const tabStop = active ?? views[0];

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const last = views.length - 1;
    const moves: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    refs.current[next]?.focus();
    const view = views[next];
    if (view) onSelect(view);
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex min-w-0 flex-nowrap gap-1 overflow-x-auto"
    >
      {views.map((view, index) => {
        const selected = view.id === active?.id;
        return (
          <button
            key={view.id}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={view.id === tabStop?.id ? 0 : -1}
            onClick={() => onSelect(view)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'focus-visible:ring-ring min-h-11 shrink-0 rounded-md border-b-2 px-3 text-sm whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none',
              selected
                ? 'border-primary text-primary font-semibold'
                : 'text-muted-foreground hover:bg-muted border-transparent',
            )}
          >
            {view.label}
          </button>
        );
      })}
    </div>
  );
}
